import { DateTime } from 'luxon';
import { ErrorPayload } from '@redux/common/types';
import {
  cancelTaskAction,
  clearGeneratedTaskMessageAction,
  clearGeneratedTaskNameAction,
  clearGeneratedTaskSummaryAction,
  clearSelectedTaskDetails,
  generateTaskMessageAction,
  getEngagementPlansAction,
  getPatientEngagementPlansAction,
  getEngagementPlanTasksAction,
  getPersonUpcomingTasksAction,
  getPersonTasksByDateRangeAction,
  getTaskDetailsAction,
  getTasksListAction,
  postNewTaskAction,
  getAssessmentsListAction,
  generateAssessmentMessageAction,
  clearSelectedAssessment,
  clearPreviewAssessmentDetails,
  getPreviewAssessmentDetailsAction,
  getAssessmentResultAction,
  setPreviewAssessmentId,
  disableEngagementPlanAction,
  createEngagementPlanAction,
  getEngagementPlaByIdnAction
} from '@redux/tasks/tasksActions';
import {
  AssessmentListItemType,
  AssessmentMessageGenerationPayloadType,
  AssessmentMessageGenerationResponseType,
  AssessmentResultType,
  EngagementPlan,
  EngagementPlanTask,
  PatientEngagementPlan,
  PreviewAssessmentDetailsType,
  TaskCreationPayloadType,
  TaskDetailsType,
  TaskMessageGenerationPayloadType,
  TaskMessageGenerationResponseType,
  TasksListItemType,
  TaskSummaryType
} from '@redux/tasks/tasksTypes';
import { ThunkParams } from '@redux/types';

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function processGetTasksListSuccess(payload: TasksListItemType[]) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTasksListAction.success(payload));
  };
}

function processGetTasksListFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTasksListAction.fail(error));
  };
}

export function getTasksListThunk(organizationId: string, selectedDate: Date) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTasksListAction.start());
    return apiClient.get(`v1/engagementPlan/tasks/${organizationId}?selectedDate=${DateTime.fromJSDate(selectedDate).toISODate() || ''}`, {
      auth: true,
      successAction: processGetTasksListSuccess,
      failAction: processGetTasksListFail
    });
  };
}

function processGetPersonUpcomingTasksSuccess(payload: TasksListItemType[]) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonUpcomingTasksAction.success(payload));
  };
}

function processGetPersonUpcomingTasksFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonUpcomingTasksAction.fail(error));
  };
}

export function getPersonUpcomingTasksThunk(personId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonUpcomingTasksAction.start());
    return apiClient.get(`v1/engagementPlan/listUpcomingTasks/${personId}`, {
      auth: true,
      successAction: processGetPersonUpcomingTasksSuccess,
      failAction: processGetPersonUpcomingTasksFail
    });
  };
}

function processGetPersonTasksByDateRangeSuccess(payload: TaskSummaryType[]) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonTasksByDateRangeAction.success(payload));
  };
}

function processGetPersonTasksByDateRangeFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonTasksByDateRangeAction.fail(error));
  };
}

export function getPersonTasksByDateRangeThunk(personId: string, startDate: Date, endDate: Date, engagementPlanId?: string | null) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPersonTasksByDateRangeAction.start());
    try {
      const planParam = engagementPlanId ? `&engagementPlanId=${engagementPlanId}` : '';
      const response = await apiClient.get(
        `v1/engagementPlan/tasks/patients/${personId}?start-date=${formatDateForAPI(startDate)}&endDate=${formatDateForAPI(endDate)}${planParam}`,
        {
          auth: true
        }
      );
      const tasks = response.data || response || [];
      dispatch(getPersonTasksByDateRangeAction.success(tasks));
      return tasks;
    } catch (error: any) {
      console.error('Failed to fetch person tasks by date range:', error);
      const errorPayload: ErrorPayload = {
        name: error?.name || 'PersonTasksByDateRangeError',
        message: error?.message || 'Failed to fetch person tasks by date range',
        status: error?.response?.status || 500
      };
      dispatch(getPersonTasksByDateRangeAction.fail(errorPayload));
      return [];
    }
  };
}

function processGetTaskDetailsSuccess(payload: TaskDetailsType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTaskDetailsAction.success(payload));
  };
}

function processGetTaskDetailsFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTaskDetailsAction.fail(error));
  };
}

export function getTaskDetailsThunk(taskId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getTaskDetailsAction.start());
    return apiClient.get(`v1/engagementPlan/task/${taskId}`, {
      auth: true,
      successAction: processGetTaskDetailsSuccess,
      failAction: processGetTaskDetailsFail
    });
  };
}

export function clearSelectedTaskDetailsThunk() {
  return function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(clearSelectedTaskDetails());
  };
}

function processPatchCancelTaskSuccess(payload: TaskDetailsType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(cancelTaskAction.success(payload));
  };
}

function processPatchCancelTaskFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(cancelTaskAction.fail(error));
  };
}

export function patchCancelTaskThunk(taskId: string, isSingleCancel: boolean) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(cancelTaskAction.start());
    return apiClient.patch(`v1/engagementPlan/task/${taskId}/cancel?including-future-tasks=${isSingleCancel ? 'false' : 'true'}`, {
      auth: true,
      successAction: processPatchCancelTaskSuccess,
      failAction: processPatchCancelTaskFail
    });
  };
}

function processPostNewTaskThunkSuccess(payload: {}) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(postNewTaskAction.success({}));
  };
}

function processPostNewTaskThunkFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(postNewTaskAction.fail(error));
  };
}

export function postNewTaskThunk(task: TaskCreationPayloadType) {
  return async function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(postNewTaskAction.start());

    return apiClient.post(`v1/engagementPlan/task`, {
      auth: true,
      data: task,
      successAction: processPostNewTaskThunkSuccess,
      failAction: processPostNewTaskThunkFail
    });
  };
}

function processGetGeneratedMessageFromTaskMessageThunkSuccess(payload: TaskMessageGenerationResponseType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(generateTaskMessageAction.success(payload));
  };
}
function processGetGeneratedMessageFromTaskMessageThunkFailure(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(generateTaskMessageAction.fail(error));
  };
}

export function getGeneratedMessageFromTaskMessageThunk(payload: TaskMessageGenerationPayloadType) {
  return async function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(generateTaskMessageAction.start());

    return apiClient.post(`v1/engagementPlan/task/generateMessage`, {
      auth: true,
      data: payload,
      successAction: processGetGeneratedMessageFromTaskMessageThunkSuccess,
      failAction: processGetGeneratedMessageFromTaskMessageThunkFailure
    });
  };
}

export function clearGeneratedTaskMessageThunk() {
  return function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(clearGeneratedTaskMessageAction());
  };
}

export function clearGeneratedTaskNameThunk() {
  return function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(clearGeneratedTaskNameAction());
  };
}

export function clearGeneratedTaskSummaryThunk() {
  return function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(clearGeneratedTaskSummaryAction());
  };
}

export function scheduleEngagementPlanThunk(engagementPlanId: string, personId: string, startDate: Date) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    try {
      const response = await apiClient.post(`v1/engagementPlan/${engagementPlanId}/schedule?patient-id=${personId}&start-date=${formatDateForAPI(startDate)}`, {
        auth: true
      });
      return response;
    } catch (error: any) {
      console.error('Failed to schedule engagement plan:', error);
      const errorPayload: ErrorPayload = {
        name: error?.name || 'ScheduleEngagementPlanError',
        message: error?.message || 'Failed to schedule engagement plan',
        status: error?.response?.status || 500
      };
      throw errorPayload;
    }
  };
}

function processGetEngagementPlansSuccess(payload: { engagementPlans: EngagementPlan[] }) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlansAction.success(payload.engagementPlans || []));
  };
}

function processGetEngagementPlansFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlansAction.fail(error));
  };
}

export function getEngagementPlansThunk(orgId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlansAction.start());
    return apiClient.get(`v1/engagementPlan?organizationId=${orgId}`, {
      auth: true,
      successAction: processGetEngagementPlansSuccess,
      failAction: processGetEngagementPlansFail
    });
  };
}

function processGetPatientEngagementPlansSuccess(payload: { engagementPlans: PatientEngagementPlan[] }) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPatientEngagementPlansAction.success(payload.engagementPlans || []));
  };
}

function processGetPatientEngagementPlansFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPatientEngagementPlansAction.fail(error));
  };
}

export function getPatientEngagementPlansThunk(patientId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPatientEngagementPlansAction.start());
    return apiClient.get(`v1/engagementPlan/patient/${patientId}`, {
      auth: true,
      successAction: processGetPatientEngagementPlansSuccess,
      failAction: processGetPatientEngagementPlansFail
    });
  };
}

export function cancelEngagementPlanThunk(patientEngagementPlanId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    return apiClient.post(`v1/engagementPlan/${patientEngagementPlanId}/cancel`, {
      auth: true
    });
  };
}

function processGetEngagementPlanTasksSuccess(payload: EngagementPlanTask[]) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlanTasksAction.success(payload));
  };
}

function processGetEngagementPlanTasksFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlanTasksAction.fail(error));
  };
}

export function getEngagementPlanTasksThunk(engagementPlanId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlanTasksAction.start());
    try {
      const response = await apiClient.get(`v1/engagementPlan/${engagementPlanId}`, {
        auth: true
      });
      const tasks = response.data?.tasks || response.tasks || [];
      dispatch(getEngagementPlanTasksAction.success(tasks));
      return tasks;
    } catch (error: any) {
      console.error('Failed to fetch engagement plan tasks:', error);
      const errorPayload: ErrorPayload = {
        name: error?.name || 'EngagementPlanTasksError',
        message: error?.message || 'Failed to fetch engagement plan tasks',
        status: error?.response?.status || 500
      };
      dispatch(getEngagementPlanTasksAction.fail(errorPayload));
      return [];
    }
  };
}

function processGetAssessmentsListSuccess(payload: AssessmentListItemType[]) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentsListAction.success(payload));
  };
}

function processGetAssessmentsListFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentsListAction.fail(error));
  };
}

export function getAssessmentsListThunk(organizationId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentsListAction.start());
    return apiClient.get(`v1/engagementPlan/assessment/organization/${organizationId}`, {
      auth: true,
      successAction: processGetAssessmentsListSuccess,
      failAction: processGetAssessmentsListFail
    });
  };
}

function processGetGeneratedMessageFromAssessmentMessageThunkSuccess(payload: AssessmentMessageGenerationResponseType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(generateAssessmentMessageAction.success(payload));
  };
}
function processGetGeneratedMessageFromAssessmentMessageThunkFailure(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(generateAssessmentMessageAction.fail(error));
  };
}

export function getGeneratedMessageFromAssessmentMessageThunk(payload: AssessmentMessageGenerationPayloadType) {
  return async function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(generateAssessmentMessageAction.start());

    return apiClient.post(`v1/engagementPlan/assessment/startingMessage/generate`, {
      auth: true,
      data: payload,
      successAction: processGetGeneratedMessageFromAssessmentMessageThunkSuccess,
      failAction: processGetGeneratedMessageFromAssessmentMessageThunkFailure
    });
  };
}

export function clearSelectedAssessmentThunk() {
  return function (dispatch: Function) {
    dispatch(clearSelectedAssessment());
  };
}

export function setPreviewAssessmentIdThunk(payload: string | null) {
  return function (dispatch: Function) {
    dispatch(setPreviewAssessmentId(payload));
  };
}

function processGetPreviewAssessmentDetailsThunkSuccess(payload: PreviewAssessmentDetailsType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPreviewAssessmentDetailsAction.success(payload));
  };
}
function processGetPreviewAssessmentDetailsThunkFailure(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getPreviewAssessmentDetailsAction.fail(error));
  };
}

export function getPreviewAssessmentDetailsThunk(assessmentTemplateId: string) {
  return async function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(getPreviewAssessmentDetailsAction.start());

    return apiClient.get(`/v1/engagementPlan/assessmentTemplate/${assessmentTemplateId}`, {
      auth: true,
      successAction: processGetPreviewAssessmentDetailsThunkSuccess,
      failAction: processGetPreviewAssessmentDetailsThunkFailure
    });
  };
}

export function clearPreviewAssessmentDetailsThunk() {
  return function (dispatch: Function) {
    dispatch(clearPreviewAssessmentDetails());
  };
}

function processGetAssessmentResultThunkSuccess(payload: AssessmentResultType) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentResultAction.success(payload));
  };
}

function processGetAssessmentResultThunkFailure(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentResultAction.fail(error));
  };
}

export function getAssessmentResultThunk(patientAssessmentId: string) {
  return async function (dispatch: Function, getState: Function, { apiClient }: ThunkParams) {
    dispatch(getAssessmentResultAction.start());

    return apiClient.get(`/v1/engagementPlan/Assessment/${patientAssessmentId}/viewResult`, {
      auth: true,
      successAction: processGetAssessmentResultThunkSuccess,
      failAction: processGetAssessmentResultThunkFailure
    });
  };
}

function processDisableEngagementPlanSuccess(payload: EngagementPlan) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(disableEngagementPlanAction.success(payload));
  };
}

function processDisableEngagementPlanFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(disableEngagementPlanAction.fail(error));
  };
}

export function disableEngagementPlanThunk(planId: string, organizationId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(disableEngagementPlanAction.start());
    return apiClient.post(`v1/engagementPlan/${planId}/disable?organization-id=${organizationId}`, {
      auth: true,
      successAction: processDisableEngagementPlanSuccess,
      failAction: processDisableEngagementPlanFail
    });
  };
}

function processCreateEngagementPlanSuccess(payload: EngagementPlan) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(createEngagementPlanAction.success(payload));
  };
}

function processCreateEngagementPlanFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(createEngagementPlanAction.fail(error));
  };
}

export function createEngagementPlanThunk(planDetails: EngagementPlan) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(createEngagementPlanAction.start());
    return apiClient.post(`v1/engagementPlan`, {
      auth: true,
      data: planDetails,
      successAction: processCreateEngagementPlanSuccess,
      failAction: processCreateEngagementPlanFail
    });
  };
}

function processGetEngagementPlanByIdSuccess(payload: EngagementPlan) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlaByIdnAction.success(payload));
  };
}

function processGetEngagementPlanByIdFail(error: ErrorPayload) {
  return function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlaByIdnAction.fail(error));
  };
}

export function getEngagementPlanByIdThunk(planId: string) {
  return async function (dispatch: Function, _: Function, { apiClient }: ThunkParams) {
    dispatch(getEngagementPlaByIdnAction.start());
    return apiClient.get(`v1/engagementPlan/${planId}`, {
      auth: true,
      successAction: processGetEngagementPlanByIdSuccess,
      failAction: processGetEngagementPlanByIdFail
    });
  };
}
